import { CODE_ALPHABET, normaliseCode } from "./codes.js";
/**
 * The ONDO code, as a mark.
 *
 * MOVED HERE FROM `apps/web-frontend/src/lib` on 4 October, because the phone has
 * to draw the same mark. Two implementations of a reversible encoding is two
 * encodings, and the second one is wrong the first time the first one changes —
 * which it is about to (OND-128). Nothing in it was ever web-specific: it computes
 * element widths and a colour name, and the SVG and the React Native views are
 * both drawn from those.
 *
 * WHAT IT IS. A work's code — `OND-K7PQ-3M2X` — drawn as a barcode whose BARS
 * ARE THE CODE and whose INK IS ITS STATE. The bars are a reversible encoding of
 * the eight payload characters, so they are the work's identity and never
 * change; the ink is a fact about a holding, so it changes hands. Two owners of
 * the same work see the same bars in different ink, and the previous owner's
 * copy goes black rather than disappearing — "never delete; end", drawn.
 *
 * FOUR LAYERS, AND EACH ANSWERS A DIFFERENT QUESTION.
 *
 *   1. The bars      — which work this is. Never changes.
 *   2. The ink       — one colour, the state. See `stateOf`.
 *   3. The add-on    — loan, agent and edition, as a SECOND smaller symbol in
 *                      the same ink. EAN-2's trick: an ISBN's price add-on can
 *                      change without touching the ISBN, and these can change
 *                      without touching the code.
 *   4. The words     — the code, and the state spelled out.
 *
 * ONE COLOUR PER CODE. The first build drew attributes as extra colour BANDS,
 * which cost it the whole of layer 2: a mark with three attributes was four
 * stripes of four colours and no longer said one thing. Attributes are a
 * supplement to a code, not a competing statement about it, so they moved into
 * layer 3 where they can be read or ignored.
 *
 * AND THE STATE IS ALSO A WORD, always. Red and green are the classic
 * colour-blind failure — around one man in twelve — and they are the most
 * commercially loaded pair in the product. A mark that can only be read by
 * someone who can tell those two apart is a mark that lies to the rest.
 *
 * WHY IT ENCODES RATHER THAN HASHES. `WorkBarcode`, which this replaces, refused
 * a generated mark in terms that apply here: "a decorative stand-in would look
 * like something the registry had measured." So the bars are made to mean
 * something. Both the code and the add-on decode — `decodeOndoCode` is the
 * proof, and the unit suite is its only caller.
 */
/**
 * The alphabet `ondo.mint_work_code()` draws from, from `@ondo/shared`.
 *
 * It moved there when the phone app needed to READ a code off a camera: the
 * reader and the writer have to agree on which thirty characters exist, and two
 * copies of that is how they stop agreeing. The reasoning for the thirty is in
 * `domain/codes.ts`.
 */
const ALPHABET = CODE_ALPHABET;
/**
 * Code 128's REAL value table. 107 patterns, typed out because it cannot be derived.
 *
 * ## This replaced a table that looked right and meant nothing
 *
 * Until 2026-10-04 the mark used a table it GENERATED: all 108 width patterns that
 * satisfy Code 128's geometry — six elements, widths 1 to 4, eleven modules, even bar
 * parity — in lexicographic order, taking the first thirty for the alphabet.
 *
 * Every one of those is a well-formed Code 128 symbol. **Not one of them means what
 * Code 128 says it means.** The generated table's symbol 0 is `1,1,1,1,4,3`; the real
 * value 0 is `2,1,2,2,2,2`, which sits at index 58 in that generated list — and the
 * alphabet only ever reached index 29. So the mark was a barcode-shaped thing that
 * every scanner on earth either failed on or, worse, read as the wrong characters.
 *
 * Nothing regressed when this changed, because nothing could read it before.
 *
 * ## How the table is checked, since it is typed
 *
 * The suite asserts four invariants — 107 entries, eleven modules each (thirteen for
 * Stop), even bar parity, all distinct — and then three **independently published bit
 * strings**: value 0 is `11011001100`, Start B is `11010010000`, Stop is
 * `1100011101011`. The invariants catch a wrong digit; the anchors catch a table that
 * is well-formed and not Code 128's.
 *
 * **Point a real scanner at a printed mark before this ships.** No test here can
 * substitute for that, and it is the one check that matters.
 */
const CODE128 = [
    [2, 1, 2, 2, 2, 2], [2, 2, 2, 1, 2, 2], [2, 2, 2, 2, 2, 1], [1, 2, 1, 2, 2, 3], [1, 2, 1, 3, 2, 2],
    [1, 3, 1, 2, 2, 2], [1, 2, 2, 2, 1, 3], [1, 2, 2, 3, 1, 2], [1, 3, 2, 2, 1, 2], [2, 2, 1, 2, 1, 3],
    [2, 2, 1, 3, 1, 2], [2, 3, 1, 2, 1, 2], [1, 1, 2, 2, 3, 2], [1, 2, 2, 1, 3, 2], [1, 2, 2, 2, 3, 1],
    [1, 1, 3, 2, 2, 2], [1, 2, 3, 1, 2, 2], [1, 2, 3, 2, 2, 1], [2, 2, 3, 2, 1, 1], [2, 2, 1, 1, 3, 2],
    [2, 2, 1, 2, 3, 1], [2, 1, 3, 2, 1, 2], [2, 2, 3, 1, 1, 2], [3, 1, 2, 1, 3, 1], [3, 1, 1, 2, 2, 2],
    [3, 2, 1, 1, 2, 2], [3, 2, 1, 2, 2, 1], [3, 1, 2, 2, 1, 2], [3, 2, 2, 1, 1, 2], [3, 2, 2, 2, 1, 1],
    [2, 1, 2, 1, 2, 3], [2, 1, 2, 3, 2, 1], [2, 3, 2, 1, 2, 1], [1, 1, 1, 3, 2, 3], [1, 3, 1, 1, 2, 3],
    [1, 3, 1, 3, 2, 1], [1, 1, 2, 3, 1, 3], [1, 3, 2, 1, 1, 3], [1, 3, 2, 3, 1, 1], [2, 1, 1, 3, 1, 3],
    [2, 3, 1, 1, 1, 3], [2, 3, 1, 3, 1, 1], [1, 1, 2, 1, 3, 3], [1, 1, 2, 3, 3, 1], [1, 3, 2, 1, 3, 1],
    [1, 1, 3, 1, 2, 3], [1, 1, 3, 3, 2, 1], [1, 3, 3, 1, 2, 1], [3, 1, 3, 1, 2, 1], [2, 1, 1, 3, 3, 1],
    [2, 3, 1, 1, 3, 1], [2, 1, 3, 1, 1, 3], [2, 1, 3, 3, 1, 1], [2, 1, 3, 1, 3, 1], [3, 1, 1, 1, 2, 3],
    [3, 1, 1, 3, 2, 1], [3, 3, 1, 1, 2, 1], [3, 1, 2, 1, 1, 3], [3, 1, 2, 3, 1, 1], [3, 3, 2, 1, 1, 1],
    [3, 1, 4, 1, 1, 1], [2, 2, 1, 4, 1, 1], [4, 3, 1, 1, 1, 1], [1, 1, 1, 2, 2, 4], [1, 1, 1, 4, 2, 2],
    [1, 2, 1, 1, 2, 4], [1, 2, 1, 4, 2, 1], [1, 4, 1, 1, 2, 2], [1, 4, 1, 2, 2, 1], [1, 1, 2, 2, 1, 4],
    [1, 1, 2, 4, 1, 2], [1, 2, 2, 1, 1, 4], [1, 2, 2, 4, 1, 1], [1, 4, 2, 1, 1, 2], [1, 4, 2, 2, 1, 1],
    [2, 4, 1, 2, 1, 1], [2, 2, 1, 1, 1, 4], [4, 1, 3, 1, 1, 1], [2, 4, 1, 1, 1, 2], [1, 3, 4, 1, 1, 1],
    [1, 1, 1, 2, 4, 2], [1, 2, 1, 1, 4, 2], [1, 2, 1, 2, 4, 1], [1, 1, 4, 2, 1, 2], [1, 2, 4, 1, 1, 2],
    [1, 2, 4, 2, 1, 1], [4, 1, 1, 2, 1, 2], [4, 2, 1, 1, 1, 2], [4, 2, 1, 2, 1, 1], [2, 1, 2, 1, 4, 1],
    [2, 1, 4, 1, 2, 1], [4, 1, 2, 1, 2, 1], [1, 1, 1, 1, 4, 3], [1, 1, 1, 3, 4, 1], [1, 3, 1, 1, 4, 1],
    [1, 1, 4, 1, 1, 3], [1, 1, 4, 3, 1, 1], [4, 1, 1, 1, 1, 3], [4, 1, 1, 3, 1, 1], [1, 1, 3, 1, 4, 1],
    [1, 1, 4, 1, 3, 1], [3, 1, 1, 1, 4, 1], [4, 1, 1, 1, 3, 1], [2, 1, 1, 4, 1, 2], [2, 1, 1, 2, 1, 4],
    [2, 1, 1, 2, 3, 2], [2, 3, 3, 1, 1, 1, 2],
];
/** Start Code B, and Stop. Named rather than indexed, so nothing reads 104 as magic. */
const START_B = 104;
const STOP = 106;
/**
 * Set B's value for a character: its ASCII code less 32.
 *
 * Derivable rather than tabulated, which is why it is the one part of Code 128 that
 * needs no checking: value 0 is a space (ASCII 32), so `'0'` is 16, `'A'` is 33, and
 * `'-'` is 13. Every character the ONDO alphabet uses, and the hyphen, is in Set B.
 *
 * Null for anything outside the printable ASCII range Set B covers, which cannot
 * happen for a normalised code and is refused rather than wrapped.
 */
function setB(character) {
    const point = character.codePointAt(0);
    if (point === undefined || point < 32 || point > 126)
        return null;
    return point - 32;
}
/**
 * Code 128's modulo-103 check character.
 *
 * The start value, plus each data value times its one-based position, modulo 103. It
 * is what makes the symbol self-checking in the way the old custom encoding was not —
 * a mis-read element now fails the sum instead of yielding a plausible wrong code.
 */
function checkValue(start, data) {
    const weighted = data.reduce((sum, value, index) => sum + value * (index + 1), start);
    return weighted % 103;
}
/**
 * The ADD-ON's own table, which is deliberately NOT Code 128.
 *
 * The supplement is EAN-2's trick — a second, smaller symbol carrying facts that can
 * change without touching the code — and no standard covers it. It keeps the
 * generated table the main symbol used to use, and its opening guard is four elements
 * of five modules, which **cannot be any Code 128 start pattern**. So a scanner reads
 * the main symbol, stops at the Stop pattern, finds no valid start in what follows,
 * and ignores it. That is the intended behaviour and the reason the guard's shape
 * matters.
 */
const ADDON_TABLE = (() => {
    const out = [];
    const walk = (acc) => {
        if (acc.length === 6) {
            const total = acc.reduce((a, b) => a + b, 0);
            const bars = (acc[0] ?? 0) + (acc[2] ?? 0) + (acc[4] ?? 0);
            if (total === 11 && bars % 2 === 0)
                out.push([...acc]);
            return;
        }
        for (let width = 1; width <= 4; width += 1) {
            acc.push(width);
            walk(acc);
            acc.pop();
        }
    };
    walk([]);
    return out.slice(0, ALPHABET.length);
})();
const ADDON_KEY = new Map(ADDON_TABLE.map((pattern, index) => [pattern.join(""), index]));
/** Opens the supplement. Four elements, five modules — no Code 128 start is this. */
const ADD_ON = [1, 1, 2, 1];
const PAYLOAD = 8;
/**
 * A code as it compares — the rule `ondo.normalise_work_code()` applies.
 *
 * Kept as a named re-export rather than deleted: this module's callers import it
 * from here, and the rule itself now lives beside the alphabet in
 * `@ondo/shared`, where the phone reads codes with it too.
 */
export const normaliseWorkCode = normaliseCode;
/** The eight payload characters as alphabet indices, or null. */
function payloadOf(code) {
    const body = normaliseWorkCode(code);
    if (body.length !== 3 + PAYLOAD || !body.startsWith("OND"))
        return null;
    const indices = [...body.slice(3)].map((character) => ALPHABET.indexOf(character));
    return indices.some((index) => index < 0) ? null : indices;
}
function formatCode(indices) {
    const letters = indices.map((index) => ALPHABET[index] ?? "");
    return `OND-${letters.slice(0, 4).join("")}-${letters.slice(4).join("")}`;
}
/** The state, spelled out — layer 4, and not optional. */
export const STATE_WORD = {
    museum: "Museum",
    active: "Active",
    awaiting: "Awaiting determination",
    "never-sold": "Never sold",
    historic: "Historic",
    undisclosed: "Not disclosed",
};
/**
 * The single fact a `WorkRecordResponse` reports, in the shape this takes.
 *
 * A one-line adapter rather than a second opinion: it renames, and decides
 * nothing.
 */
export function factsFromWorkRecord(code, extra = {}) {
    return {
        sold: code.sold,
        authorityAttested: code.authorityAttested,
        soldToInstitution: code.soldToInstitution,
        onLoanToInstitution: code.onLoanToInstitution,
        agentHands: code.hands,
        ...extra,
    };
}
/**
 * The number out of an edition string, when there is one to take.
 *
 * `works.edition_text` is TEXT because "AP 2/4" and "unnumbered" are real
 * catalogue values and neither is a number — 20260804090700 made that argument
 * when it added the column. So this reads the leading ordinal of an `n/m` and
 * gives up on anything else rather than guessing: an artist's proof has no
 * number in the edition's sense, and inventing one would put a figure on the
 * mark that the catalogue does not claim.
 *
 * SINCE OND-65 THERE IS A STRUCTURED ANSWER FOR A WORK THAT IS A RECORDED COPY:
 * `ondo.works.edition_copy_number` beside `edition_copy_class`, read through
 * `ondo.work_edition()` and rendered by `editionCopyNumbering()` in
 * `domain/work.ts`. This guesser stays, and is still right for the far larger
 * case it was written for — a single cast in a collector's hands whose edition
 * is described in free text and whose siblings are not on ONDO. PREFER THE
 * STRUCTURED NUMBER WHERE A SCREEN HAS ONE, because this one reads `n/m` only
 * and will never know that "AP 2" is a proof.
 */
export function editionNumberOf(editionText) {
    if (!editionText)
        return null;
    const match = /^\s*(\d+)\s*\/\s*\d+\s*$/.exec(editionText);
    if (!match)
        return null;
    const value = Number(match[1]);
    return Number.isFinite(value) && value > 0 ? value : null;
}
/**
 * The ink, and the order is the decision: `historic → blue → red → yellow →
 * green`, the first that is true.
 *
 * `historic` comes first because it is a fact about the reader's own holding and
 * outranks anything about the work — a work you no longer hold is your historic
 * record whatever has happened to it since.
 *
 * BLUE OVER RED, because a museum acquisition is also a standing transfer and
 * the two collide. Once an institution owns a work, "active in the market" is
 * exactly what it is not.
 *
 * UNDISCLOSED IS NOT A STEP IN THE ORDER, it is what happens when the facts a
 * step needs are absent — and WHICH facts it needs depends on how far down it
 * got. A null `sold` undisclosed the mark outright: nothing below can be decided
 * without it. A null `authorityAttested` only matters once `sold` is known
 * FALSE, because yellow and green are the two halves of "no transfer stands" and
 * nothing above them consults a signature. So a screen that knows a work has
 * sold and nothing about who signed it still draws red, rather than going grey
 * for want of an answer to a question red never asked.
 */
export function stateOf(facts) {
    if (facts.historic)
        return "historic";
    if (facts.sold === null)
        return "undisclosed";
    if (facts.soldToInstitution === true)
        return "museum";
    if (facts.sold)
        return "active";
    if (facts.authorityAttested === null)
        return "undisclosed";
    return facts.authorityAttested ? "never-sold" : "awaiting";
}
/** What the add-on will carry, read off the same facts. */
export function attributesOf(facts) {
    return {
        loan: facts.onLoanToInstitution === true,
        // `hands` is true of every work; the add-on says so only when somebody
        // actually holds a mandate, or it would be printed on everything.
        hands: facts.hasAgent ? (facts.agentHands ?? 0) : 0,
        edNo: facts.editionNumber ?? null,
        edSize: facts.editionSize ?? null,
    };
}
/**
 * The add-on's payload, in the SAME thirty-symbol alphabet as the code.
 *
 * Fixed order — loan, then agent, then edition — so a reader who has learned the
 * shape does not relearn it per work. A number `n` is `ALPHABET[n - 1]`, capped
 * at 29 values; `Z` (index 29) in the edition's number slot means UNNUMBERED,
 * which is how an artist's proof says it has no ordinal without claiming one.
 */
export function attributeSymbols(attributes) {
    const value = (n) => Math.max(0, Math.min(28, n - 1));
    const symbols = [];
    if (attributes.loan)
        symbols.push(ALPHABET.indexOf("M"));
    if (attributes.hands > 0)
        symbols.push(ALPHABET.indexOf("A"), value(attributes.hands));
    if (attributes.edSize !== null && attributes.edSize > 0) {
        symbols.push(ALPHABET.indexOf("E"), attributes.edNo !== null && attributes.edNo > 0 ? value(attributes.edNo) : 29, value(attributes.edSize));
    }
    return symbols;
}
/** The caption printed over the add-on, so it reads without the table. */
export function captionFor(attributes) {
    const parts = [];
    if (attributes.loan)
        parts.push("LOAN");
    if (attributes.hands > 0)
        parts.push(`AG ${attributes.hands}`);
    if (attributes.edSize !== null && attributes.edSize > 0) {
        parts.push(`ED ${attributes.edNo !== null && attributes.edNo > 0 ? attributes.edNo : "–"}/${attributes.edSize}`);
    }
    return parts.join(" · ");
}
/**
 * The bars, or null.
 *
 * NULL IS A REAL ANSWER and every failure takes the same path: a code that does
 * not normalise to `OND` plus eight characters of the alphabet renders NOTHING.
 * `WorkBarcode` took the same stance for a photograph it could not sample, and
 * the reason carries over — better nothing than a mark that looks measured and
 * measured the wrong thing.
 */
export function encodeOndoCode(code, attributes) {
    const indices = payloadOf(code);
    if (!indices)
        return null;
    /*
     * WHAT THE BARS CARRY: the NORMALISED code, `ONDK7PQ3M2X`.
     *
     * Not the printed form with its hyphens, and not the bare eight payload characters.
     * The normalised form is what `ondo.normalise_work_code()` compares on and what
     * `readCode` in `domain/codes.ts` answers with — so a scanner's output is already
     * the canonical thing, and a third party's scanner yields something
     * self-identifying rather than eight context-free characters.
     *
     * The hyphens are a printing convention and stay in the printed text.
     */
    const text = `OND${indices.map((index) => ALPHABET[index] ?? "").join("")}`;
    const values = [];
    for (const character of text) {
        const value = setB(character);
        // Cannot happen for a normalised code — every character is A–Z or 2–9 — but a
        // missing value would otherwise encode as a wrong symbol rather than as nothing.
        if (value === null)
            return null;
        values.push(value);
    }
    const main = [...(CODE128[START_B] ?? [])];
    for (const value of values)
        main.push(...(CODE128[value] ?? []));
    main.push(...(CODE128[checkValue(START_B, values)] ?? []));
    main.push(...(CODE128[STOP] ?? []));
    const symbols = attributeSymbols(attributes);
    const add = symbols.length
        ? [...ADD_ON, ...symbols.flatMap((index) => [...(ADDON_TABLE[index] ?? [])])]
        : [];
    return { main, add };
}
/**
 * Read a mark back to its code and attributes.
 *
 * Exists so the encoding can be PROVEN reversible rather than asserted to be, and the
 * unit suite is its only caller. A mark nobody can read back is a texture, and this is
 * what stops it quietly becoming one.
 *
 * **It now verifies the check character**, which the custom encoding had none of. That
 * is not decoration: a decoder that accepts any well-formed run of symbols cannot tell
 * a correct mark from a mis-measured one, and "it round-trips" would then be a claim
 * about this function rather than about the symbol.
 */
export function decodeOndoCode(encoded) {
    const symbols = symbolsOf(encoded.main);
    if (symbols === null)
        return null;
    // Start, at least one data character, the check, and Stop.
    if (symbols.length < 4)
        return null;
    if (symbols[0] !== START_B)
        return null;
    if (symbols[symbols.length - 1] !== STOP)
        return null;
    const data = symbols.slice(1, -2);
    const check = symbols[symbols.length - 2];
    if (check !== checkValue(START_B, data))
        return null;
    const text = data.map((value) => String.fromCodePoint(value + 32)).join("");
    const body = normaliseCode(text);
    if (body.length !== 3 + PAYLOAD || !body.startsWith("OND"))
        return null;
    const letters = [...body.slice(3)];
    if (letters.some((letter) => !ALPHABET.includes(letter)))
        return null;
    const attributes = [];
    if (encoded.add.length) {
        if (!sameWidths(encoded.add.slice(0, ADD_ON.length), ADD_ON))
            return null;
        const addSymbols = [];
        for (let cursor = ADD_ON.length; cursor < encoded.add.length; cursor += 6) {
            const index = ADDON_KEY.get(encoded.add.slice(cursor, cursor + 6).join(""));
            if (index === undefined)
                return null;
            addSymbols.push(index);
        }
        let cursor = 0;
        while (cursor < addSymbols.length) {
            const kind = ALPHABET[addSymbols[cursor++] ?? -1];
            if (kind === "M")
                attributes.push("loan");
            else if (kind === "A")
                attributes.push(`agent ${(addSymbols[cursor++] ?? 0) + 1}`);
            else if (kind === "E") {
                const number = addSymbols[cursor++] ?? 0;
                const size = addSymbols[cursor++] ?? 0;
                attributes.push(`ed ${number === 29 ? "–" : number + 1}/${size + 1}`);
            }
            else
                return null;
        }
    }
    return { code: `OND-${letters.slice(0, 4).join("")}-${letters.slice(4).join("")}`, attributes };
}
/**
 * Split a run of element widths back into Code 128 symbol values.
 *
 * Six elements each until the last symbol, which is Stop's seven. Returns null for a
 * run that does not divide cleanly or that contains a pattern the table does not hold —
 * both of which mean the widths did not come from this encoder.
 */
function symbolsOf(main) {
    const values = [];
    let at = 0;
    while (at < main.length) {
        const left = main.length - at;
        // Stop is the only seven-element symbol, and it is always last.
        const size = left === 7 ? 7 : 6;
        if (left < size)
            return null;
        const key = main.slice(at, at + size).join(",");
        const value = CODE128.findIndex((pattern) => pattern.join(",") === key);
        if (value < 0)
            return null;
        values.push(value);
        at += size;
    }
    return values;
}
function sameWidths(a, b) {
    return a.join() === b.join();
}
/** The number of elements in the start symbol, for the renderer's guard wells. */
export const START_ELEMENTS = 6;
/** The number of elements in the stop symbol. */
export const STOP_ELEMENTS = 7;
/** The whole mark, or null when the code cannot be drawn. */
export function toOndoCodeView(code, facts) {
    const attributes = attributesOf(facts);
    const encoded = encodeOndoCode(code, attributes);
    const indices = payloadOf(code);
    if (!encoded || !indices)
        return null;
    const state = stateOf(facts);
    const caption = captionFor(attributes);
    const letters = indices.map((index) => ALPHABET[index] ?? "");
    return {
        encoded,
        state,
        word: STATE_WORD[state],
        caption,
        left: letters.slice(0, 4).join(""),
        right: letters.slice(4).join(""),
        label: `ONDO code ${formatCode(indices)}. ${STATE_WORD[state]}.${caption ? ` ${caption}.` : ""}`,
    };
}
//# sourceMappingURL=ondoCode.js.map